import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { formatDate } from 'utils/format';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderEvent } from 'models/order-event';
import { useLazyQuery } from '@apollo/client';
import { commentsTemplate } from 'apollo/templates/order';
import { OrderInputType } from 'models/order-input/shared';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import DOMPurify from 'dompurify';
import AdditionalComments from 'components/order-flow/shared/additional-comments';

export type CommentsHandle = {
    commit: () => string;
};

type CommentsProps = {
    isCommentRequired?: boolean;
};

const Comments = forwardRef<CommentsHandle, CommentsProps>(({ isCommentRequired }, ref) => {
    const { t } = useTranslation(['orders']);
    const { order, setOrder } = useContext(ActionOrderContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const [oldComments, setOldComments] = useState<OrderEvent[]>([]);
    const [comment, setComment] = useState(order.comment ?? '');

    const [fetchComments] = useLazyQuery(commentsTemplate, {
        fetchPolicy: 'network-only',
        onCompleted({ comments }) {
            setOldComments(comments);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (order._id && order.inputType !== OrderInputType.NEW) {
            fetchComments({ variables: { orderId: order._id } });
        }
    }, [order._id, order.inputType, fetchComments]);

    useEffect(() => {
        setComment(order.comment ?? '');
    }, [order._id]);

    const commit = () => {
        setOrder((prev) => (prev.comment === comment ? prev : { ...prev, comment }));
        return comment;
    };

    useImperativeHandle(ref, () => ({ commit }), [comment]);

    return (
        <>
            {order.historicalComments?.length > 0 && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        {t('orders:ORIGINAL_ORDER_COMMENTS')}
                    </Typography>
                    <AdditionalComments additionalComments={order.historicalComments} />
                </Box>
            )}
            {oldComments.length > 0 && (
                <Box mb={3}>
                    {oldComments.map((event) => (
                        <Box key={event.id ?? `${event.date}-${event.version}`} mb={2}>
                            <Typography variant="subtitle2">
                                {formatDate(new Date(event.date), 'datetime')} – {event.user?.fullName} ({t('VERSION')}{' '}
                                {event.version})
                            </Typography>

                            <Box
                                mt={0.5}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(event.message ?? ''),
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            <TextField
                multiline
                minRows={6}
                fullWidth
                required={isCommentRequired}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onBlur={commit}
                placeholder={t('COMMENT')}
            />
        </>
    );
});

Comments.displayName = 'Comments';

export default Comments;
